import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Save, Sparkles } from 'lucide-react';
import HistoryDialog from './HistoryDialog';
import UpdatedAtBadge from './UpdatedAtBadge';
import DraftBanner from './DraftBanner';
import { useAutoSaveDraft } from '../../hooks/useAutoSaveDraft';

const defaults = {
  badge: 'EST. 1979 • KUALA LUMPUR, MALAYSIA',
  title_line1: 'Our Society,',
  title_line2: 'Our Responsibility.',
  description:
    'For over four decades, EWRF has empowered underserved communities through education, counselling, and welfare – building brighter futures, one life at a time.',
  highlight_words: ['education', 'counselling', 'welfare'],
  primary_cta_label: 'EXPLORE OUR PROGRAMMES',
  primary_cta_target: 'programmes',
  secondary_cta_label: '♥ DONATE NOW',
  secondary_cta_target: 'donate',
};

const HeroContentManager = () => {
  const [d, setD] = useState(defaults);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    try {
      const res = await adminApi.get('/hero');
      if (res.data) setD({ ...defaults, ...res.data });
    } catch (e) { /* ignore */ }
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const { hasDraft, draftAt, readDraft, discardDraft, markSaved } = useAutoSaveDraft({
    storageKey: 'draft:hero',
    data: d,
    enabled: loaded,
    savedAt: d.updated_at,
  });

  const restoreDraft = () => {
    const draft = readDraft();
    if (draft) setD({ ...defaults, ...draft });
    discardDraft();
  };

  const set = (k, v) => setD((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...d,
        highlight_words: (typeof d.highlight_words === 'string'
          ? d.highlight_words.split(',')
          : d.highlight_words
        )
          .map((w) => w.trim())
          .filter(Boolean),
      };
      await adminApi.post('/hero', payload);
      const res = await adminApi.get('/hero');
      if (res.data) setD({ ...defaults, ...res.data });
      markSaved(res.data || payload);
      toast({ title: 'Hero saved', description: 'Changes are live on the homepage.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const highlightsAsString = Array.isArray(d.highlight_words)
    ? d.highlight_words.join(', ')
    : (d.highlight_words || '');

  return (
    <div className="space-y-6" data-testid="hero-content-manager">
      {hasDraft && (
        <DraftBanner
          draftAt={draftAt}
          onRestore={restoreDraft}
          onDiscard={discardDraft}
          testid="hero-draft-banner"
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#1565C0]" />
          <h2 className="text-2xl font-bold">Hero Section &mdash; &quot;Our Society, Our Responsibility&quot;</h2>
          <UpdatedAtBadge updatedAt={d.updated_at} />
        </div>
        <HistoryDialog collection="hero" docId="hero" onRestored={load} />
      </div>
      <p className="text-sm text-gray-600 -mt-2">
        Controls the main headline block right under the slider. Words listed in &quot;Highlight Words&quot; appear in gold in the description.
      </p>

      <Card>
        <CardHeader><CardTitle className="text-base">Badge & Headline</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Badge (top small pill)</Label>
            <Input
              value={d.badge}
              onChange={(e) => set('badge', e.target.value)}
              data-testid="hero-badge-input"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Title — Line 1</Label>
              <Input
                value={d.title_line1}
                onChange={(e) => set('title_line1', e.target.value)}
                data-testid="hero-title1-input"
              />
            </div>
            <div>
              <Label>Title — Line 2 (gold italic)</Label>
              <Input
                value={d.title_line2}
                onChange={(e) => set('title_line2', e.target.value)}
                data-testid="hero-title2-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Description</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Paragraph</Label>
            <Textarea
              rows={4}
              value={d.description}
              onChange={(e) => set('description', e.target.value)}
              data-testid="hero-description-input"
            />
          </div>
          <div>
            <Label>Highlight Words (comma-separated — these words render in gold)</Label>
            <Input
              value={highlightsAsString}
              onChange={(e) => set('highlight_words', e.target.value)}
              placeholder="education, counselling, welfare"
              data-testid="hero-highlights-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Buttons</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Primary (gold)</p>
            <div>
              <Label>Label</Label>
              <Input
                value={d.primary_cta_label}
                onChange={(e) => set('primary_cta_label', e.target.value)}
                data-testid="hero-primary-label"
              />
            </div>
            <div>
              <Label>Scroll target section id</Label>
              <Input
                value={d.primary_cta_target}
                onChange={(e) => set('primary_cta_target', e.target.value)}
                placeholder="programmes"
                data-testid="hero-primary-target"
              />
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Secondary (outline)</p>
            <div>
              <Label>Label</Label>
              <Input
                value={d.secondary_cta_label}
                onChange={(e) => set('secondary_cta_label', e.target.value)}
                data-testid="hero-secondary-label"
              />
            </div>
            <div>
              <Label>Scroll target section id</Label>
              <Input
                value={d.secondary_cta_target}
                onChange={(e) => set('secondary_cta_target', e.target.value)}
                placeholder="donate"
                data-testid="hero-secondary-target"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1565C0] hover:bg-[#0F4C9E]"
          data-testid="hero-save-btn"
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? 'Saving…' : 'Save Hero Section'}
        </Button>
      </div>
    </div>
  );
};

export default HeroContentManager;
